import { PositionedBall, UrnBallType } from './urn.model';

const BALL_RADIUS = 13;
const STROKE_WIDTH = 1.2;

/**
 * Returns the horizontal bounds [xMin, xMax] of the jar interior at a given y.
 * The jar is a tall mason-jar shape: short vertical neck (68..152),
 * shoulder curve outward, straight sides, rounded bottom.
 *
 * Jar interior regions (top to bottom in SVG coords):
 * - y < 30: above jar (no bounds)
 * - 30..42: neck, straight walls ~68..152
 * - 42..90: shoulder, curves from ~68..152 out to ~22..198
 * - 90..286: straight body, constant width ~22..198
 * - 286..318: rounded bottom corners
 * - y > 318: below jar (no bounds)
 */
function jarBounds(y: number): [number, number] | null {
  if (y < 30 || y > 318) {
    return null;
  }

  // Neck region: straight vertical walls
  if (y <= 42) {
    return [68 + BALL_RADIUS, 152 - BALL_RADIUS];
  }

  // Shoulder region: curves from neck width to body width
  if (y <= 90) {
    const t = (y - 42) / 48; // 0 at y=42, 1 at y=90
    // Ease-out curve for smoother shoulder
    const ease = 1 - (1 - t) * (1 - t);
    const xMin = 68 - ease * 46; // 68 → 22
    const xMax = 152 + ease * 46; // 152 → 198
    return [xMin + BALL_RADIUS, xMax - BALL_RADIUS];
  }

  // Straight body
  if (y <= 286) {
    return [22 + BALL_RADIUS, 198 - BALL_RADIUS];
  }

  // Rounded bottom corners
  const t = (y - 286) / 32; // 0 at y=286, 1 at y=318
  const squeeze = t * t * 36;
  return [22 + squeeze + BALL_RADIUS, 198 - squeeze - BALL_RADIUS];
}

/**
 * Gravity-based ball packing: shuffle all balls, then place each at the
 * lowest available position inside the jar boundary.
 */
export function packBalls(counts: Record<string, number>, ballTypes: UrnBallType[]): PositionedBall[] {
  // Build flat list of ball types to place
  const toPlace: UrnBallType[] = [];
  for (const bt of ballTypes) {
    const n = counts[bt.id] ?? 0;
    for (let i = 0; i < n; i++) {
      toPlace.push(bt);
    }
  }

  // Shuffle (seeded-ish via simple Fisher-Yates with a deterministic seed based on counts)
  const seed = toPlace.length;
  let rng = seed * 9301 + 49297;
  for (let i = toPlace.length - 1; i > 0; i--) {
    rng = (rng * 9301 + 49297) % 233280;
    const j = rng % (i + 1);
    [toPlace[i], toPlace[j]] = [toPlace[j], toPlace[i]];
  }

  const placed: PositionedBall[] = [];
  const r = BALL_RADIUS;
  const diameter = r * 2;
  const typeCounters: Record<string, number> = {};

  for (const bt of toPlace) {
    let bestPos: { cx: number; cy: number } | null = null;

    // Scan from bottom upward
    for (let y = 318 - r; y >= 30 + r; y -= 2) {
      const bounds = jarBounds(y);
      if (!bounds) {
        continue;
      }
      const [xMin, xMax] = bounds;
      if (xMax - xMin < diameter) {
        continue;
      }

      // Try x positions across the width
      for (let x = xMin; x <= xMax; x += 2) {
        let overlaps = false;
        for (const p of placed) {
          const dx = p.cx - x;
          const dy = p.cy - y;
          const minDist = diameter + STROKE_WIDTH;
          if (dx * dx + dy * dy < minDist * minDist) {
            overlaps = true;
            break;
          }
        }
        if (!overlaps) {
          bestPos = { cx: x, cy: y };
          break;
        }
      }
      if (bestPos) {
        break;
      }
    }

    if (bestPos) {
      const typeIdx = typeCounters[bt.id] ?? 0;
      typeCounters[bt.id] = typeIdx + 1;
      placed.push({ id: `${bt.id}-${typeIdx}`, cx: bestPos.cx, cy: bestPos.cy, r, type: bt });
    }
  }

  return placed;
}

export function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a;
}

export function simplifyFraction(numerator: number, denominator: number): { num: number; den: number } {
  if (denominator === 0) {
    return { num: 0, den: 0 };
  }
  const d = gcd(numerator, denominator);
  return { num: numerator / d, den: denominator / d };
}
